/**
 * Modals ([data-dmi-modal="name"], opened with [data-dmi-modal-open="name"], closed with
 * [data-dmi-modal-close] or Escape) and slide-in drawers (`group/drawer` root that gets `is-open`).
 * Both fade and slide through an `is-open` class (transitions in theme.css); the element gets
 * `hidden` once the close transition is done. A modal dispatches `dmi:modal:open` / `dmi:modal:close`.
 */
import { qs, qsa, reduceMotion } from '@theme/theme-utils';

export const MODAL_MS = 340;
const modalTimers = new WeakMap();
const openModals = new Set();

function syncBodyScroll() {
  document.body.classList.toggle('overflow-hidden', openModals.size > 0);
}

const modalEl = (name) => qs(`[data-dmi-modal="${name}"]`);

export function openModal(name, { stopPropagationEvent } = {}) {
  if (!name) return;
  if (stopPropagationEvent) stopPropagationEvent.stopPropagation();
  const el = modalEl(name);
  if (!el) return;
  clearTimeout(modalTimers.get(el));
  el.classList.remove('hidden');
  el.setAttribute('aria-hidden', 'false');
  void el.offsetWidth; // flush the display change, so the transition runs
  el.classList.add('is-open');
  openModals.add(name);
  syncBodyScroll();
  el.dispatchEvent(new CustomEvent('dmi:modal:open'));
}

export function closeModal(name) {
  if (!name) return;
  const el = modalEl(name);
  if (!el) return;
  el.classList.remove('is-open');
  el.setAttribute('aria-hidden', 'true');
  openModals.delete(name);
  syncBodyScroll();
  clearTimeout(modalTimers.get(el));
  modalTimers.set(el, setTimeout(() => el.classList.add('hidden'), reduceMotion() ? 0 : MODAL_MS));
  el.dispatchEvent(new CustomEvent('dmi:modal:close'));
}

export function closeAllModals() {
  [...openModals].forEach(closeModal);
  // Also close any open modal not tracked (e.g. opened before bind)
  qsa('[data-dmi-modal].is-open').forEach((el) => {
    const name = el.getAttribute('data-dmi-modal');
    if (name) closeModal(name);
  });
}

export const DRAWER_MS = 320;
const drawerTimers = new WeakMap();

export function setDrawer(el, open, { instant = false } = {}) {
  if (!el) return;
  clearTimeout(drawerTimers.get(el));
  el.setAttribute('aria-hidden', open ? 'false' : 'true');
  document.body.classList.toggle('overflow-hidden', open);
  if (open) {
    el.hidden = false;
    el.classList.remove('hidden');
    if (instant) el.classList.add('is-open');
    else requestAnimationFrame(() => el.classList.add('is-open'));
    return;
  }
  el.classList.remove('is-open');
  const hide = () => {
    el.classList.add('hidden');
    el.hidden = true;
  };
  if (instant) hide();
  else drawerTimers.set(el, setTimeout(hide, DRAWER_MS));
}

export const openDrawer = (el, opts) => setDrawer(el, true, opts);
export const closeDrawer = (el, opts) => setDrawer(el, false, opts);
export const isDrawerOpen = (el) => el?.getAttribute('aria-hidden') === 'false';

document.addEventListener('click', (e) => {
  const openBtn = e.target.closest('[data-dmi-modal-open]');
  if (openBtn) return openModal(openBtn.getAttribute('data-dmi-modal-open'), { stopPropagationEvent: e });
  if (e.target.closest('[data-dmi-modal-close]')) closeModal(e.target.closest('[data-dmi-modal]')?.getAttribute('data-dmi-modal'));
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || openModals.size === 0) return;
  const last = [...openModals].pop();
  if (last) closeModal(last);
});
